import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Map, MapPin } from 'lucide-react';
import {
  Modal,
  Button,
  TextInput,
  Select,
  SearchableComboBox,
  RichTextInput,
} from '@kardume/ui';
import { useNotification } from '@kardume/hooks';
import {
  backofficeApi,
  extractErrorMessage,
  UF_OPTIONS,
  lookupCep,
  maskCep,
  onlyCepDigits,
} from '@kardume/services';
import type {
  ComboItem,
  MerchantDto,
  MerchantDuplicateDto,
  MerchantSource,
  QuickMerchantRequestDto,
} from '@kardume/types';

/**
 * Cadastro rápido de uma empresa no guia da cidade (card 231, regras §38).
 *
 * Existe ao lado do `MerchantFormModal`, e não dentro dele, porque são dois
 * cadastros com propósitos diferentes: aquele é o comercial completo, com
 * documento, responsável, plano e checklist de onboarding; este tem que caber
 * em um minuto. Misturá-los faria o formulário rápido herdar as validações do
 * lento - que é exatamente o motivo de hoje a vitrine só mostrar parceiro.
 *
 * O que este formulário PEDE é o mínimo: nome, categoria, origem e fonte. O que
 * ele **oferece** é o mesmo endereço do cadastro do lojista (busca por CEP,
 * rua/número/complemento/bairro), as localidades atendidas e a descrição -
 * porque o dado que o curador tem na mão na hora do cadastro é o mais barato
 * que existe, e não ter onde digitá-lo obrigava a reabrir a empresa no
 * formulário completo depois. Nada disso é obrigatório: a empresa entra no guia
 * com nome e categoria, como sempre entrou.
 *
 * O único campo que este formulário acrescenta em relação ao mínimo técnico é a
 * **origem** (§38.3), e ela é obrigatória: sem ela ninguém responde seis meses
 * depois se aquele telefone veio do estabelecimento, de uma lista pública ou do
 * chute de alguém.
 */
const SOURCE_OPTIONS: { value: MerchantSource; label: string }[] = [
  { value: 'BACKOFFICE', label: 'Equipe Kardume' },
  { value: 'GUIDE', label: 'Curadoria do guia' },
  { value: 'IMPORT', label: 'Importação autorizada' },
  { value: 'SELF_ONBOARDING', label: 'A própria empresa' },
];

interface Props {
  isOpen: boolean;
  categories: ComboItem[];
  /** Cidades ativas, para as localidades atendidas. Mesma lista do cadastro completo. */
  localities: ComboItem[];
  localitiesLoading?: boolean;
  onClose: () => void;
  onCreated: (merchant: MerchantDto) => void;
}

/**
 * Estado da tela.
 *
 * Campos de texto ficam como `string` (nunca `undefined`) para o input ser
 * sempre controlado; o que vai vazio para o core é decidido no envio, por
 * `trimmed`.
 */
interface GuideForm {
  name: string;
  categoryId: string;
  source: MerchantSource;
  sourceReference: string;
  zipCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  phone: string;
  whatsapp: string;
  instagram: string;
  website: string;
  description: string;
  localityIds: string[];
}

const empty: GuideForm = {
  name: '',
  categoryId: '',
  source: 'GUIDE',
  sourceReference: '',
  zipCode: '',
  street: '',
  number: '',
  complement: '',
  district: '',
  city: '',
  state: '',
  phone: '',
  whatsapp: '',
  instagram: '',
  website: '',
  description: '',
  localityIds: [],
};

/** Texto em branco não é dado: vai como ausente, e não como string vazia. */
const trimmed = (value: string): string | undefined => value.trim() || undefined;

/**
 * A descrição vem de um editor de HTML, então "vazia" pode ser `<p><br></p>`.
 * Sem esta checagem toda empresa cadastrada sem descrição gravaria uma marcação
 * que a página pública renderiza como um parágrafo em branco.
 */
const richTextOrUndefined = (html: string): string | undefined => {
  const semTags = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
  return semTags ? html : undefined;
};

const MerchantGuideModal: React.FC<Props> = ({
  isOpen,
  categories,
  localities,
  localitiesLoading,
  onClose,
  onCreated,
}) => {
  const { showNotification } = useNotification();
  const [form, setForm] = useState<GuideForm>(empty);
  const [duplicates, setDuplicates] = useState<MerchantDuplicateDto[]>([]);
  const [cepLoading, setCepLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setForm(empty);
      setDuplicates([]);
    }
  }, [isOpen]);

  const set = <K extends keyof GuideForm>(key: K, value: GuideForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleLocality = (id: string) =>
    setForm((prev) => ({
      ...prev,
      localityIds: prev.localityIds.includes(id)
        ? prev.localityIds.filter((l) => l !== id)
        : [...prev.localityIds, id],
    }));

  const canSave = useMemo(
    () => form.name.trim().length > 1 && !!form.categoryId && form.sourceReference.trim().length > 0,
    [form],
  );

  /**
   * Busca o CEP e preenche o resto do endereço.
   *
   * Só sobrescreve o que o ViaCEP soube responder: quem já digitou o bairro à
   * mão não o perde porque o CEP genérico da cidade não traz bairro.
   */
  const handleCep = async (raw: string): Promise<void> => {
    const cep = onlyCepDigits(raw);
    set('zipCode', cep);
    if (cep.length !== 8) return;
    setCepLoading(true);
    const found = await lookupCep(cep);
    setCepLoading(false);
    if (!found) return;
    setForm((prev) => ({
      ...prev,
      street: found.street || prev.street,
      district: found.district || prev.district,
      city: found.city || prev.city,
      state: found.state || prev.state,
    }));
  };

  /**
   * A checagem de duplicidade acontece ao SAIR do campo de nome, não a cada
   * tecla: com debounce por tecla, o operador vê o aviso aparecer e sumir
   * enquanto digita, e para de olhar para ele.
   */
  const checkDuplicates = () => {
    const name = form.name.trim();
    if (name.length < 4) {
      setDuplicates([]);
      return;
    }
    backofficeApi
      .findMerchantDuplicates(name, form.city.trim() || undefined)
      .then(setDuplicates)
      .catch(() => setDuplicates([]));
  };

  const save = (ignoreDuplicates: boolean) => {
    const payload: QuickMerchantRequestDto = {
      name: form.name.trim(),
      categoryId: form.categoryId,
      source: form.source,
      sourceReference: form.sourceReference.trim(),
      zipCode: trimmed(form.zipCode),
      street: trimmed(form.street),
      number: trimmed(form.number),
      complement: trimmed(form.complement),
      district: trimmed(form.district),
      city: trimmed(form.city),
      state: trimmed(form.state),
      phone: trimmed(form.phone),
      whatsapp: trimmed(form.whatsapp),
      instagram: trimmed(form.instagram),
      website: trimmed(form.website),
      description: richTextOrUndefined(form.description),
      localityIds: form.localityIds.length > 0 ? form.localityIds : undefined,
      ignoreDuplicates,
    };
    setSaving(true);
    backofficeApi
      .quickCreateMerchant(payload)
      .then((merchant) => {
        showNotification('Empresa cadastrada no guia.', 'success');
        onCreated(merchant);
        onClose();
      })
      .catch((e) => showNotification(extractErrorMessage(e, 'Não foi possível cadastrar.'), 'error'))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Cadastrar no guia"
      width="720px"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!canSave || saving}
            isLoading={saving}
            onClick={() => save(duplicates.length > 0)}
          >
            {duplicates.length > 0 ? 'Cadastrar assim mesmo' : 'Cadastrar'}
          </Button>
        </>
      }
    >
      <Hint>
        Cria a empresa <strong>visível no guia</strong> e <strong>sem parceria</strong>. O selo de
        parceiro é outro caminho, com motivo e histórico. Só nome, categoria, origem e fonte são
        obrigatórios - o resto entra agora ou depois, pelo cadastro completo.
      </Hint>

      <Grid>
        <Field className="full">
          <label htmlFor="guide-name">Nome *</label>
          <TextInput
            id="guide-name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            onBlur={checkDuplicates}
            placeholder="Padaria do Centro"
          />
        </Field>

        <Field className="full">
          <label htmlFor="guide-category">Categoria *</label>
          <SearchableComboBox
            items={categories}
            valueId={form.categoryId}
            onChange={(id) => set('categoryId', id)}
            placeholder="Buscar categoria..."
          />
        </Field>

        {/* §38.3 - a procedência. Obrigatória, e por isso vem logo abaixo do
            nome e não numa aba "avançado": é parte do cadastro, não detalhe. */}
        <Field>
          <label htmlFor="guide-source">Origem *</label>
          <Select
            id="guide-source"
            options={SOURCE_OPTIONS}
            value={form.source}
            onChange={(e) => set('source', e.target.value as MerchantSource)}
          />
        </Field>
        <Field>
          <label htmlFor="guide-source-ref">Fonte *</label>
          <TextInput
            id="guide-source-ref"
            value={form.sourceReference}
            onChange={(e) => set('sourceReference', e.target.value)}
            placeholder="Ex.: lista da associação comercial, 2026"
          />
        </Field>
      </Grid>

      {/* Mesmo endereço do cadastro do lojista, mesmas colunas no banco. Sem
          isto o guia gravava só uma linha livre, que nenhuma tela sabia editar
          campo a campo - e que a geocodificação por CEP não conseguia usar. */}
      <Section>
        <SectionTitle>
          <MapPin size={16} /> Endereço
        </SectionTitle>
        <Grid>
          <Field>
            <label htmlFor="guide-cep">{cepLoading ? 'CEP (buscando...)' : 'CEP'}</label>
            <TextInput
              id="guide-cep"
              value={maskCep(form.zipCode)}
              onChange={(e) => handleCep(e.target.value)}
              placeholder="00000-000"
            />
          </Field>
          <Field>
            <label htmlFor="guide-street">Rua</label>
            <TextInput
              id="guide-street"
              value={form.street}
              onChange={(e) => set('street', e.target.value)}
            />
          </Field>

          <Field>
            <label htmlFor="guide-number">Número</label>
            <TextInput
              id="guide-number"
              value={form.number}
              onChange={(e) => set('number', e.target.value)}
            />
          </Field>
          <Field>
            <label htmlFor="guide-complement">Complemento</label>
            <TextInput
              id="guide-complement"
              value={form.complement}
              onChange={(e) => set('complement', e.target.value)}
              placeholder="Ex.: Sala 101"
            />
          </Field>

          <Field>
            <label htmlFor="guide-district">Bairro</label>
            <TextInput
              id="guide-district"
              value={form.district}
              onChange={(e) => set('district', e.target.value)}
            />
          </Field>
          <Field>
            <label htmlFor="guide-city">Cidade</label>
            <TextInput
              id="guide-city"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              onBlur={checkDuplicates}
            />
          </Field>

          <Field>
            <label htmlFor="guide-state">Estado (UF)</label>
            <SearchableComboBox
              items={UF_OPTIONS}
              valueId={form.state || undefined}
              onChange={(id) => set('state', id)}
              placeholder="UF"
            />
          </Field>
        </Grid>
      </Section>

      {/* A localidade é o que faz a empresa aparecer na vitrine de uma cidade
          vizinha sem endereço lá. Quem cadastra pelo guia costuma saber disso
          na hora - e não sabia onde dizer. */}
      <Section>
        <SectionTitle>
          <Map size={16} /> Localidades atendidas
        </SectionTitle>
        <ChipRow>
          {localitiesLoading && <Muted>Carregando cidades...</Muted>}
          {!localitiesLoading && localities.length === 0 && (
            <Muted>Nenhuma cidade cadastrada - cadastre em Localidades.</Muted>
          )}
          {localities.map((locality) => (
            <LocalityChip
              key={locality.id}
              type="button"
              $selected={form.localityIds.includes(locality.id)}
              onClick={() => toggleLocality(locality.id)}
            >
              {locality.label}
            </LocalityChip>
          ))}
        </ChipRow>
      </Section>

      <Section>
        <Grid>
          <Field>
            <label htmlFor="guide-phone">Telefone</label>
            <TextInput
              id="guide-phone"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
            />
          </Field>
          <Field>
            <label htmlFor="guide-whatsapp">WhatsApp</label>
            <TextInput
              id="guide-whatsapp"
              value={form.whatsapp}
              onChange={(e) => set('whatsapp', e.target.value)}
            />
          </Field>

          <Field>
            <label htmlFor="guide-instagram">Instagram</label>
            <TextInput
              id="guide-instagram"
              value={form.instagram}
              onChange={(e) => set('instagram', e.target.value)}
            />
          </Field>
          <Field>
            <label htmlFor="guide-website">Site</label>
            <TextInput
              id="guide-website"
              value={form.website}
              onChange={(e) => set('website', e.target.value)}
            />
          </Field>
        </Grid>
      </Section>

      <Section>
        <RichTextInput
          label="Descrição"
          placeholder="Descreva a empresa, diferenciais, etc."
          value={form.description}
          onChange={(html) => set('description', html)}
          minHeight={120}
        />
      </Section>

      {/* §38.4 - avisa, não bloqueia. Duas padarias com o mesmo nome em bairros
          diferentes é caso real, e quem está com a lista na mão sabe disso
          melhor do que a heurística. */}
      {duplicates.length > 0 && (
        <Duplicates>
          <h4>
            <AlertTriangle size={15} /> Já existe empresa parecida
          </h4>
          <ul>
            {duplicates.map((d) => (
              <li key={d.id}>
                <strong>{d.tradeName || d.name}</strong>
                {d.city ? ` · ${d.city}` : ''}
                {d.partner ? ' · parceira' : ''}
              </li>
            ))}
          </ul>
          <p>
            Confira antes de seguir: uma página duplicada divide favoritos e avaliações, e o
            estrago só aparece meses depois.
          </p>
        </Duplicates>
      )}
    </Modal>
  );
};

export default MerchantGuideModal;

/* -------------------------------------------------------------------------- */

const Hint = styled.p`
  margin: 0 0 1rem;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textMuted};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.85rem;

  .full {
    grid-column: 1 / -1;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  margin-top: 1.25rem;
`;

const SectionTitle = styled.h4`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0 0 0.6rem;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${(p) => p.theme.textSecondary};
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  label {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Muted = styled.span`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
`;

const LocalityChip = styled.button<{ $selected: boolean }>`
  border: 1px solid ${(p) => (p.$selected ? p.theme.primary : p.theme.border)};
  background: ${(p) => (p.$selected ? p.theme.surfaceAlt : 'transparent')};
  color: ${(p) => (p.$selected ? p.theme.primary : p.theme.textMuted)};
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => p.theme.primary};
    color: ${(p) => p.theme.primary};
  }
`;

const Duplicates = styled.div`
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.warning}1a;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    color: ${(p) => p.theme.textPrimary};
  }
  ul {
    margin: 0 0 0.5rem;
    padding-left: 1.1rem;
    font-size: 0.85rem;
    color: ${(p) => p.theme.textPrimary};
  }
  p {
    margin: 0;
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;
